import React, { useState } from 'react';
import { AlertTriangle, Trash2, User, Mail, Calendar } from 'lucide-react';
import { DeleteRequest } from '../types';
import { Modal } from './Modal';

interface ApproveDeleteRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: DeleteRequest | null;
  onConfirm: () => void;
  loading?: boolean;
}

export const ApproveDeleteRequestModal: React.FC<ApproveDeleteRequestModalProps> = ({
  isOpen,
  onClose,
  request,
  onConfirm,
  loading = false,
}) => {
  const [confirmed, setConfirmed] = useState(false);

  const handleClose = () => {
    setConfirmed(false);
    onClose();
  };

  const handleConfirm = () => {
    if (confirmed) {
      onConfirm();
    }
  };

  if (!request) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Approve Delete Request">
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-bold text-red-900 mb-2">
                Permanent Account Deletion Warning
              </h3>
              <p className="text-sm text-red-800">
                You are about to permanently delete this user account. This action cannot be undone and will result in the permanent loss of all associated data.
              </p>
            </div>
          </div>
        </div>

        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
          <h4 className="text-sm font-semibold text-slate-900 mb-3">Account to be Deleted:</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-slate-500" />
              <span className="text-sm text-slate-700">
                <span className="font-medium">Name:</span> {request.name}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-slate-500" />
              <span className="text-sm text-slate-700">
                <span className="font-medium">Email:</span> {request.email}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-slate-500" />
              <span className="text-sm text-slate-700">
                <span className="font-medium">Account Type:</span> {request.accountType === 'provider' ? 'Provider' : 'Service User'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-slate-500" />
              <span className="text-sm text-slate-700">
                <span className="font-medium">Request Date:</span>{' '}
                {new Date(request.createdAt).toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })}
              </span>
            </div>
          </div>
        </div>

        <div className="border border-slate-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Trash2 className="h-4 w-4 text-red-600" />
            What will be deleted:
          </h4>
          <ul className="space-y-2">
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <span className="text-red-600 font-bold mt-0.5">•</span>
              <span>User account and login credentials</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <span className="text-red-600 font-bold mt-0.5">•</span>
              <span>Complete profile information and personal data</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <span className="text-red-600 font-bold mt-0.5">•</span>
              <span>Booking history and transaction records</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <span className="text-red-600 font-bold mt-0.5">•</span>
              <span>Reviews given and received</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <span className="text-red-600 font-bold mt-0.5">•</span>
              <span>Messages and communication history</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <span className="text-red-600 font-bold mt-0.5">•</span>
              <span>All other data associated with this account</span>
            </li>
          </ul>
        </div>

        <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
          <p className="text-sm text-amber-900">
            <span className="font-semibold">Important:</span> Before approving, ensure you have reviewed the account details and deletion reason carefully. Active bookings or pending transactions should be handled appropriately before account deletion.
          </p>
        </div>

        <div className="border border-slate-300 rounded-lg p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
            />
            <span className="text-sm text-slate-700">
              I understand that this action is permanent and cannot be undone. I have reviewed all account information and confirm that I want to approve this deletion request.
            </span>
          </label>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <button
            onClick={handleClose}
            disabled={loading}
            className="px-6 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!confirmed || loading}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Processing...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4" />
                Approve & Delete Account
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};
